import type { NormalizedChart, NormalizedHoroscope, NormalizedHoroscopeItem, PalaceId } from './domain.js';
import type { Feature } from './rules.js';
import { extractNatalFeatures } from './featureExtractor.js';

function evidence(scope: NormalizedHoroscopeItem['scope'], parts: readonly string[]): string {
  return ['HOROSCOPE', scope, ...parts].join(':').toUpperCase();
}

function itemFeatures(item: NormalizedHoroscopeItem): Feature[] {
  const features: Feature[] = [
    {
      id: `horoscope.${item.scope}.index`,
      scope: item.scope,
      kind: 'horoscope_index',
      value: item.index,
      evidenceRefs: [evidence(item.scope, ['INDEX', String(item.index)])],
      evidenceGroup: `${item.scope}-cycle-core`,
    },
    {
      id: `horoscope.${item.scope}.heavenly_stem`,
      scope: item.scope,
      kind: 'horoscope_heavenly_stem',
      value: item.heavenlyStem,
      evidenceRefs: [evidence(item.scope, ['HEAVENLY-STEM', item.heavenlyStem])],
      evidenceGroup: `${item.scope}-cycle-core`,
    },
    {
      id: `horoscope.${item.scope}.earthly_branch`,
      scope: item.scope,
      kind: 'horoscope_earthly_branch',
      value: item.earthlyBranch,
      evidenceRefs: [evidence(item.scope, ['EARTHLY-BRANCH', item.earthlyBranch])],
      evidenceGroup: `${item.scope}-cycle-core`,
    },
  ];

  if (item.nominalAge !== undefined) {
    features.push({
      id: `horoscope.${item.scope}.nominal_age`,
      scope: item.scope,
      kind: 'horoscope_nominal_age',
      value: item.nominalAge,
      evidenceRefs: [evidence(item.scope, ['NOMINAL-AGE', String(item.nominalAge)])],
      evidenceGroup: `${item.scope}-cycle-core`,
    });
  }

  for (const [natalPalaceId, cyclePalaceId] of Object.entries(item.cyclePalaceByNatalPalace) as [PalaceId, PalaceId][]) {
    features.push({
      id: `horoscope.${item.scope}.natal_palace.${natalPalaceId}.cycle_palace`,
      scope: item.scope, kind: 'horoscope_palace_mapping_value', value: cyclePalaceId,
      evidenceRefs: [evidence(item.scope, ['PALACE-MAP', natalPalaceId, cyclePalaceId])],
      evidenceGroup: `${item.scope}-palace-map`,
    });
    features.push({
      id: `horoscope.${item.scope}.natal_palace.${natalPalaceId}.cycle_palace.${cyclePalaceId}`,
      scope: item.scope,
      kind: 'horoscope_palace_mapping',
      value: true,
      evidenceRefs: [evidence(item.scope, ['PALACE-MAP', natalPalaceId, cyclePalaceId])],
      evidenceGroup: `${item.scope}-palace-map`,
    });
  }

  for (const transformation of item.mutagens) {
    features.push({
      id: `horoscope.${item.scope}.mutagen.${transformation.mutagen}.star.${transformation.starId}`,
      scope: item.scope,
      kind: 'horoscope_four_transformation',
      value: true,
      evidenceRefs: [evidence(item.scope, ['MUTAGEN', transformation.mutagen, transformation.starId, transformation.basis])],
      evidenceGroup: `${item.scope}-transformations`,
    });
  }

  for (const [natalPalaceId, stars] of Object.entries(item.movingStarsByNatalPalace) as [PalaceId, NonNullable<NormalizedHoroscopeItem['movingStarsByNatalPalace'][PalaceId]>][]) {
    for (const star of stars) {
      features.push({
        id: `horoscope.${item.scope}.natal_palace.${natalPalaceId}.has_moving_star.${star.id}`,
        scope: item.scope,
        kind: 'horoscope_moving_star',
        value: true,
        evidenceRefs: [evidence(item.scope, ['MOVING-STAR', natalPalaceId, star.id])],
        evidenceGroup: `${item.scope}-moving-stars`,
      });
    }
  }

  return features;
}

export function extractHoroscopeFeatures(horoscope: NormalizedHoroscope): Feature[] {
  return [
    ...itemFeatures(horoscope.decadal),
    ...itemFeatures(horoscope.minor),
    ...itemFeatures(horoscope.yearly),
    ...itemFeatures(horoscope.monthly),
    ...itemFeatures(horoscope.daily),
    ...itemFeatures(horoscope.hourly),
  ].sort((a, b) => a.id.localeCompare(b.id));
}

export function extractNatalAndHoroscopeFeatures(chart: NormalizedChart, horoscope: NormalizedHoroscope): Feature[] {
  if (chart.calculation.chartFingerprint !== horoscope.natalChartFingerprint) {
    throw new Error('Horoscope does not belong to the supplied natal chart fingerprint');
  }
  return [...extractNatalFeatures(chart), ...extractHoroscopeFeatures(horoscope)].sort((a, b) => a.id.localeCompare(b.id));
}
